import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faHourglassHalf,
  faCircleCheck,
  faRotateLeft,
  faListCheck,
  faArrowRightArrowLeft,
  faShieldHalved,
  faHandshakeAngle,
  faBoxOpen,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/routes';
import { useAuthStore } from '@/stores/auth.store';
import { dashboardService } from '@/services/dashboard.service';
import { KpiCard, KpiCardSkeleton } from './components/KpiCard';
import { QueueTable, QueueTableSkeleton } from './components/QueueTable';
import { RecentActivityFeed, RecentActivitySkeleton } from './components/RecentActivityFeed';
import { RequestCenterCard, RequestCenterCardSkeleton } from './components/RequestCenterCard';
import { AttentionRequired, AttentionRequiredSkeleton } from './components/AttentionRequired';
import { inspectionService } from '@/services/inspection.service';

/** Deep-link into the My Inspections tab whose count this KPI shows. */
const tabTo = (tab: string) => `${ROUTES.MY_INSPECTIONS}?tab=${tab}`;

export function EngineerDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.firstName ?? 'there';
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard-engineer'],
    queryFn: dashboardService.getEngineerDashboard,
    staleTime: 60_000,
  });

  // The KPI row reads the SAME endpoint the My Inspections tabs read, so a card
  // and the list it opens can never disagree. The dashboard payload's own
  // counts use different predicates (completedToday; returned without the
  // status filter; in-progress including returned) and would have shown one
  // number while the destination showed another.
  const userId = user?.userId ?? '';
  const { data: counts, isLoading: countsLoading } = useQuery({
    queryKey: ['my-inspections-status-counts', userId],
    queryFn: () => inspectionService.statusCounts({ assignedEngineerId: userId }),
    enabled: !!userId,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Welcome back, {firstName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your assigned inspections and tasks.</p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load dashboard. Check your connection and refresh.
        </div>
      )}

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          KPIs
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading || countsLoading ? (
            Array.from({ length: 6 }).map((_, i) => <KpiCardSkeleton key={i} />)
          ) : data ? (
            <>
              {/* Every card below opens the My Inspections tab whose filter
                  produced its number — same endpoint, same predicate. */}
              <KpiCard
                label="Assigned"
                value={counts?.assigned ?? 0}
                icon={faClipboardList}
                iconCls="bg-blue-500/10 text-blue-700"
                to={tabTo('assigned')}
              />
              <KpiCard
                label="In Progress"
                value={counts?.inProgress ?? 0}
                icon={faHourglassHalf}
                iconCls="bg-amber-500/10 text-amber-700"
                to={tabTo('in_progress')}
              />
              <KpiCard
                label="Returned to Me"
                value={counts?.returned ?? 0}
                icon={faRotateLeft}
                iconCls="bg-red-500/10 text-red-700"
                to={tabTo('returned')}
              />
              <KpiCard
                label="Transfer Pending"
                value={counts?.transferPending ?? 0}
                icon={faArrowRightArrowLeft}
                iconCls="bg-orange-500/10 text-orange-700"
                to={tabTo('transfer_pending')}
              />
              <KpiCard
                label="Completed"
                value={counts?.completed ?? 0}
                icon={faCircleCheck}
                iconCls="bg-green-500/10 text-green-700"
                to={tabTo('completed')}
              />
              {/* Deliberately NOT clickable: there is no task list route to
                  land on, and a card that navigates nowhere is worse than one
                  that plainly does not navigate. */}
              <KpiCard
                label="Tasks Pending"
                value={data.kpis.tasksPending}
                icon={faListCheck}
                iconCls="bg-violet-500/10 text-violet-700"
              />
            </>
          ) : null}
        </div>
      </section>

      {/* Attention Required — real counts only, zero rows filtered out. */}
      {isLoading || countsLoading ? (
        <AttentionRequiredSkeleton />
      ) : data ? (
        <AttentionRequired
          items={[
            {
              id: 'returned',
              icon: faRotateLeft,
              label: 'Returned inspections',
              hint: 'Sent back by operations for revision',
              count: counts?.returned ?? 0,
              to: tabTo('returned'),
              emphasis: true,
            },
            {
              id: 'transfer',
              icon: faArrowRightArrowLeft,
              label: 'Transfer requests',
              hint: 'Transfers offered to you',
              count: data.requestCenter.transferRequests,
              to: ROUTES.REQUEST_CENTER,
            },
            {
              id: 'takeover',
              icon: faHandshakeAngle,
              label: 'Takeover requests',
              hint: 'Requests to take over your inspections',
              count: data.requestCenter.takeoverRequests,
              to: ROUTES.REQUEST_CENTER,
            },
            {
              id: 'authorization',
              icon: faShieldHalved,
              label: 'Authorization requests',
              hint: 'Cases waiting on you to act',
              count: data.requestCenter.authorizationRequests,
              to: ROUTES.AUTHORIZATION_CENTER,
              emphasis: true,
            },
          ]}
        />
      ) : null}

      {/* Request Center */}
      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Requests
        </h2>
        {isLoading ? (
          <RequestCenterCardSkeleton />
        ) : data ? (
          <RequestCenterCard counts={data.requestCenter} />
        ) : null}
      </section>

      {/* Job Pool shortcut */}
      <section
        onClick={() => navigate(ROUTES.JOB_POOL)}
        className="flex cursor-pointer items-center gap-4 rounded-xl border border-dashed border-primary/30 bg-primary/5 px-5 py-4 hover:bg-primary/10 transition-colors"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-500/10 text-violet-700">
          <FontAwesomeIcon icon={faBoxOpen} className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Job Pool</p>
          <p className="text-xs text-muted-foreground">Browse and claim unassigned inspections</p>
        </div>
        <div className="ml-auto text-xs text-primary font-medium">View Pool →</div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <>
              <QueueTableSkeleton title="My Queue" />
              <QueueTableSkeleton title="Returned Inspections" />
            </>
          ) : data ? (
            <>
              <QueueTable
                title="My Queue"
                items={data.queues.myQueue}
                showEngineer={false}
                emptyMessage="No inspections assigned to you."
                viewAllTo={ROUTES.MY_INSPECTIONS}
                viewAllLabel="View all my inspections"
              />
              <QueueTable
                title="Returned Inspections"
                items={data.queues.returnedInspections}
                showEngineer={false}
                emptyMessage="No returned inspections."
                viewAllTo={tabTo('returned')}
                viewAllLabel="View all returned inspections"
              />
            </>
          ) : null}
        </div>

        <div>
          {isLoading ? (
            <RecentActivitySkeleton />
          ) : data ? (
            <RecentActivityFeed items={data.recentActivity} />
          ) : null}
        </div>
      </section>
    </div>
  );
}
